import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { Operation, MissionFilter } from '../types/operation';
import { useCart } from '../contexts/CartContext';
import { useFieldAuth } from '../contexts/FieldContext';
import { CartItem } from '../types/cart';
import { getOperations, OperationFilter } from '../services/operationService';
import ModernBackground from '../components/common/ModernBackground';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isShadowTheme = (theme: any) => theme.name === 'shadowGrid';
const isModernTheme = (theme: any) => theme.name === 'modernWarfare';
const isNightOps = (theme: any) => theme.name === 'nightOps';

const cardBackground = (theme: any) =>
  isShadowTheme(theme) ? 'rgba(0, 0, 0, 0.35)' : theme.colors.surface;

const cardBorder = (theme: any) =>
  isShadowTheme(theme) ? 'rgba(0, 212, 255, 0.3)' : theme.colors.borderLight;

const cardShadow = (theme: { name: string; shadows: { card: string } }) =>
  theme.name === 'shadowGrid' ? '0 18px 32px rgba(0, 0, 0, 0.5)' : theme.shadows.card;

const PageBackground = styled.div<{ $shadow?: boolean }>`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  padding: 20px;
  padding-top: 100px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding: 10px;
    padding-top: 90px;
  }
`;

const DottedOverlay = styled.div`
  display: none;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.text};
  position: relative;
  z-index: 3;
  font-family: ${({ theme }) => {
    if (theme.name === 'shadowGrid') return "'Courier New', monospace";
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return 'inherit';
  }};
  
  @media (max-width: 1240px) {
    max-width: 95%;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 10px;
  text-shadow: 0 0 10px ${({ theme }) => withAlpha(theme.colors.primary, 0.5)};
  text-transform: uppercase;
  letter-spacing: 2px;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 15px;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 30px 0;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.4;
    padding: 0 10px;
  }
`;

const ControlsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const SearchAndSort = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  background: ${({ theme }) => isShadowTheme(theme) ? 'rgba(255, 255, 255, 0.1)' : theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  font-family: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 10px ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SortSelect = styled.select`
  padding: 12px 16px;
  background: ${({ theme }) => isShadowTheme(theme) ? 'rgba(255, 255, 255, 0.1)' : theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  cursor: pointer;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 10px ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  }

  option {
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MainContent = styled.div`
  display: flex;
  gap: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const FilterSection = styled.div`
  width: 280px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileFilterHeader = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
`;

const MobileFilterButton = styled.button`
  padding: 12px 24px;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => isShadowTheme(theme) ? 'white' : '#00121f'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

const FilterOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  z-index: 998;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileFilterPanel = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 320px;
  height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  border-right: 2px solid ${({ theme }) => theme.colors.primary};
  padding: 80px 20px 20px;
  transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  transition: transform 0.3s ease;
  z-index: 999;
  overflow-y: auto;
  
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 320px) {
    width: 100%;
  }
`;

const MobileFilterHeader2 = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const MobileFilterTitle = styled.h3`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  text-transform: uppercase;
`;

const MobileFilterClose = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 20px;
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const FilterSearchBox = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const FilterSearchInput = styled.input`
  flex: 1;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px 0 0 8px;
  padding: 10px 12px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 10px ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const SearchButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 0 8px 8px 0;
  color: ${({ theme }) => isShadowTheme(theme) ? 'white' : '#00121f'};
  font-size: 12px;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const FilterParameters = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    border: none;
    background: none;
    padding: 0;
    margin-bottom: 20px;
  }
`;

const ParameterTitle = styled.h3`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 15px;
  text-transform: uppercase;
  font-weight: bold;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 15px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    gap: 15px;
    margin-bottom: 25px;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 8px 0;
  }
`;

const Checkbox = styled.input`
  margin-right: 12px;
  appearance: none;
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surface};
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:checked {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:checked::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${({ theme }) => isShadowTheme(theme) ? 'white' : '#00121f'};
    font-size: 12px;
    font-weight: bold;
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const RangeSlider = styled.div`
  margin-bottom: 20px;
  
  input[type="range"] {
    width: 100%;
    height: 6px;
    background: ${({ theme }) => theme.colors.surface};
    border-radius: 3px;
    outline: none;
    appearance: none;
    
    &::-webkit-slider-thumb {
      appearance: none;
      width: 18px;
      height: 18px;
      background: ${({ theme }) => theme.colors.primary};
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    }
    
    &::-moz-range-thumb {
      width: 18px;
      height: 18px;
      background: ${({ theme }) => theme.colors.primary};
      border-radius: 50%;
      cursor: pointer;
      border: none;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    }
  }
`;

const RangeValues = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 8px;
  font-weight: bold;
`;

const FilterButton = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => isShadowTheme(theme) ? 'white' : '#00121f'};
  font-size: 14px;
  font-weight: bold;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  margin-bottom: 20px;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

const FilterStats = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 15px;
  padding: 15px;
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.1)};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  
  div {
    margin-bottom: 5px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ContentArea = styled.div`
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MissionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const MissionCard = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 0;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => cardShadow(theme)};
  
  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.card};
  }
`;

const MissionLink = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  height: 100%;
`;

const MissionImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.1)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  font-size: 14px;
  position: relative;
  background-size: cover !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
  border-radius: 12px 12px 0 0;
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const MissionInfo = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 10px;
`;

const MissionId = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.primary};
  background: rgba(0, 0, 0, 0.7);
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
`;

const MissionMatch = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.primary};
  background: rgba(0, 0, 0, 0.7);
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
`;

const MissionHeader = styled.div`
  margin-bottom: 10px;
`;

const MissionCode = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 5px;
  text-transform: uppercase;
`;

const MissionTitle = styled.h3`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  line-height: 1.2;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const MissionDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 15px 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MissionFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  gap: 15px;
`;

const MissionPrice = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "inherit"};
`;

const AddToCartButton = styled.button`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => isShadowTheme(theme) ? 'white' : '#00121f'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-size: 0.9rem;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`;


const LoadingSpinner = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.2rem;
  padding: 40px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.textMuted};
  
  h3 {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 10px;
    font-size: 1.5rem;
  }
  
  p {
    margin-bottom: 20px;
    font-size: 1rem;
    line-height: 1.5;
  }
`;

const MissionsPage: React.FC = () => {
  const theme = useTheme();
  const isShadowGrid = theme.name === 'shadowGrid';
  const isModernWarfare = theme.name === 'modernWarfare';
  const isNightOpsMode = theme.name === 'nightOps';

  const [missions, setMissions] = useState<Operation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<MissionFilter>({
    gameTypes: [],
    difficultyLevels: [],
    minPrice: 1000,
    maxPrice: 5000,
    unitSizes: []
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isFieldAuthenticated } = useFieldAuth();

  // Handle URL parameters on component load
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlFilters: MissionFilter = {
      gameTypes: [],
      difficultyLevels: [],
      minPrice: 1000,
      maxPrice: 5000,
      unitSizes: []
    };
    
    // Обрабатываем типы игр
    const gameTypesParam = searchParams.get('gameTypes');
    if (gameTypesParam) {
      urlFilters.gameTypes = [gameTypesParam]; // Добавляем как один тип
    }
    
    // Обрабатываем уровень сложности
    const difficultyParam = searchParams.get('difficulty');
    if (difficultyParam) {
      urlFilters.difficultyLevels = [difficultyParam];
    }
    
    // Обрабатываем цену
    const priceParam = searchParams.get('maxPrice');
    if (priceParam) {
      urlFilters.maxPrice = parseInt(priceParam);
    }
    
    // Применяем фильтры из URL к состоянию
    setFilter(urlFilters);
    
    // Обновляем счетчик активных фильтров
    let count = 0;
    if (urlFilters.gameTypes && urlFilters.gameTypes.length > 0) count++;
    if (urlFilters.difficultyLevels && urlFilters.difficultyLevels.length > 0) count++;
    if (urlFilters.unitSizes && urlFilters.unitSizes.length > 0) count++;
    if (urlFilters.maxPrice !== 5000) count++;
    
    setActiveFiltersCount(count);
    
    // Сразу загружаем миссии с примененными фильтрами
    loadMissionsWithFilters(urlFilters, searchTerm, currentPage);
    
  }, [location.search]);

  const loadMissionsWithFilters = async (filterToUse: MissionFilter, searchTermToUse: string, page: number) => {
    setLoading(true);
    setError('');
    
    try {
      
      // Преобразуем MissionFilter в OperationFilter
      const operationFilter: OperationFilter = {};
      
      // Обрабатываем типы игр
      if (filterToUse.gameTypes && filterToUse.gameTypes.length > 0) {
        // Берем первый выбранный тип (или можно логику изменить для множественного выбора)
        operationFilter.type = filterToUse.gameTypes[0];
      }

      // Обрабатываем уровни сложности
      if (filterToUse.difficultyLevels && filterToUse.difficultyLevels.length > 0) {
        operationFilter.difficulties = filterToUse.difficultyLevels;
      }

      // Обрабатываем цену
      if (filterToUse.minPrice !== undefined) {
        operationFilter.minPrice = filterToUse.minPrice;
      }
      if (filterToUse.maxPrice !== undefined && filterToUse.maxPrice !== 5000) {
        operationFilter.maxPrice = filterToUse.maxPrice;
      }

      // Обрабатываем поисковый термин
      if (searchTermToUse) {
        operationFilter.searchTerm = searchTermToUse;
      }

      // Обрабатываем размер команды (преобразуем в минимальное и максимальное количество игроков)
      if (filterToUse.unitSizes && filterToUse.unitSizes.length > 0) {
        // Находим минимальный и максимальный размер команды из выбранных
        let minPlayers = Infinity;
        let maxPlayers = 0;

        filterToUse.unitSizes.forEach(size => {
          switch (size) {
            case '2-5':
              minPlayers = Math.min(minPlayers, 2);
              maxPlayers = Math.max(maxPlayers, 5);
              break;
            case '6-10':
              minPlayers = Math.min(minPlayers, 6);
              maxPlayers = Math.max(maxPlayers, 10);
              break;
            case '11-20':
              minPlayers = Math.min(minPlayers, 11);
              maxPlayers = Math.max(maxPlayers, 20);
              break;
            case '21+':
              minPlayers = Math.min(minPlayers, 21);
              maxPlayers = Math.max(maxPlayers, 100); // Предполагаем максимум 100
              break;
          }
        });

        if (minPlayers !== Infinity) {
          operationFilter.minPlayers = minPlayers;
        }
        if (maxPlayers > 0) {
          operationFilter.maxPlayers = maxPlayers;
        }
      }

      const data = await getOperations(page, 6, operationFilter);

      if (data.operations.length === 0) {
      } else {
      }

      setMissions(data.operations);
      setTotalCount(data.totalCount);
    } catch (err) {
      console.error('Error loading missions:', err);
      setError('Failed to load missions data');
    } finally {
      setLoading(false);
    }
  };

  const sortMissions = (missions: Operation[], sortBy: string): Operation[] => {
    const sorted = [...missions];
    
    switch (sortBy) {
      case 'price-asc':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return sorted.sort((a, b) => b.price - a.price);
      case 'name':
        return sorted.sort((a, b) => (a.title || a.name || '').localeCompare(b.title || b.name || ''));
      case 'difficulty':
        const difficultyOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3, 'Expert': 4 };
        return sorted.sort((a, b) => (difficultyOrder[a.difficulty as keyof typeof difficultyOrder] || 0) - (difficultyOrder[b.difficulty as keyof typeof difficultyOrder] || 0));
      default:
        return sorted;
    }
  };

  // Загрузка миссий при изменении фильтров или страницы (НЕ при первой загрузке)
  useEffect(() => {
    // Skip initial load during component initialization
    const isInitialLoad = currentPage === 1 && 
                         (filter.gameTypes?.length || 0) === 0 && 
                         (filter.difficultyLevels?.length || 0) === 0 && 
                         (filter.unitSizes?.length || 0) === 0 && 
                         filter.maxPrice === 5000 && 
                         searchTerm === '';
    
    if (!isInitialLoad) {
      loadMissionsWithFilters(filter, searchTerm, currentPage);
    }
  }, [currentPage, filter, searchTerm]); // Добавляем filter и searchTerm в зависимости

  // Обновление фильтра gameType (только один выбор)
  const handleGameTypeChange = (type: string) => {
    setFilter(prev => {
      const gameTypes = prev.gameTypes || [];
      
      // Если этот тип уже выбран, снимаем выбор (делаем массив пустым)
      if (gameTypes.includes(type)) {
        return {
          ...prev,
          gameTypes: []
        };
      } else {
        // Иначе выбираем только этот тип (заменяем весь массив)
        return {
          ...prev,
          gameTypes: [type]
        };
      }
    });
  };

  // Обновление фильтра difficultyLevels
  const handleDifficultyChange = (level: string) => {
    setFilter(prev => {
      const difficultyLevels = prev.difficultyLevels || [];
      const updatedLevels = difficultyLevels.includes(level)
        ? difficultyLevels.filter(l => l !== level)
        : [...difficultyLevels, level];
      
      return {
        ...prev,
        difficultyLevels: updatedLevels
      };
    });
  };

  // Обновление фильтра unitSizes
  const handleUnitSizeChange = (size: string) => {
    setFilter(prev => {
      const unitSizes = prev.unitSizes || [];
      const updatedSizes = unitSizes.includes(size)
        ? unitSizes.filter(s => s !== size)
        : [...unitSizes, size];
      
      return {
        ...prev,
        unitSizes: updatedSizes
      };
    });
  };

  // Обновление фильтра цены
  const handlePriceChange = (value: number) => {
    setFilter(prev => ({
      ...prev,
      minPrice: 1000,
      maxPrice: value
    }));
  };

  // Поиск
  const handleSearch = () => {
    applyFilters();
  };

  // Подсчет количества активных фильтров
  useEffect(() => {
    let count = 0;
    
    if (filter.gameTypes && filter.gameTypes.length > 0) count++;
    if (filter.difficultyLevels && filter.difficultyLevels.length > 0) count++;
    if (filter.unitSizes && filter.unitSizes.length > 0) count++;
    if (searchTerm) count++;
    if (filter.maxPrice !== 5000) count++;
    
    setActiveFiltersCount(count);
  }, [filter, searchTerm]);

  const applyFilters = () => {
    setCurrentPage(1); // При применении фильтров сбрасываем на первую страницу
    setIsMobileFilterOpen(false); // Закрываем мобильную панель фильтров
    // Перезагрузка произойдет автоматически благодаря useEffect с зависимостями [currentPage, filter, searchTerm]
  };

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  const closeMobileFilter = () => {
    setIsMobileFilterOpen(false);
  };

  // Функция для добавления миссии в корзину
  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>, mission: Operation) => {
    e.preventDefault(); // Останавливаем всплытие события, чтобы не переходить на страницу миссии
    e.stopPropagation();
    
    const cartItem: CartItem = {
      id: mission.id,
      name: mission.title || mission.name || 'Mission',
      description: mission.description,
      price: mission.price,
      quantity: 1,
      image: mission.images?.[0]
    };
    
    addItem(cartItem);
  };

  const FilterContent = () => (
    <>
      <FilterSearchBox>
        <FilterSearchInput 
          placeholder="Search missions..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <SearchButton onClick={handleSearch}>Search</SearchButton>
      </FilterSearchBox>
      
      <FilterParameters>
        <ParameterTitle>Mission Type</ParameterTitle>
        <CheckboxGroup>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox" 
              checked={filter.gameTypes?.includes('Airsoft')}
              onChange={() => handleGameTypeChange('Airsoft')}
            />
            Airsoft
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.gameTypes?.includes('Paintball')}
              onChange={() => handleGameTypeChange('Paintball')}
            />
            Paintball
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.gameTypes?.includes('Speedball')}
              onChange={() => handleGameTypeChange('Speedball')}
            />
            Speedball
          </CheckboxLabel>
        </CheckboxGroup>
      </FilterParameters>
        
      <FilterParameters>
        <ParameterTitle>Difficulty Level</ParameterTitle>
        <CheckboxGroup>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.difficultyLevels?.includes('Easy')}
              onChange={() => handleDifficultyChange('Easy')}
            />
            Easy
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.difficultyLevels?.includes('Medium')}
              onChange={() => handleDifficultyChange('Medium')}
            />
            Medium
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.difficultyLevels?.includes('Hard')}
              onChange={() => handleDifficultyChange('Hard')}
            />
            Hard
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.difficultyLevels?.includes('Expert')}
              onChange={() => handleDifficultyChange('Expert')}
            />
            Expert
          </CheckboxLabel>
        </CheckboxGroup>
      </FilterParameters>
        
      <FilterParameters>
        <ParameterTitle>Price Range</ParameterTitle>
        <RangeSlider>
          <input 
            type="range" 
            min="1000" 
            max="5000" 
            value={filter.maxPrice}
            onChange={(e) => handlePriceChange(Number(e.target.value))}
          />
          <RangeValues>
            <span>$1,000</span>
            <span>${(filter.maxPrice || 5000).toLocaleString()}</span>
          </RangeValues>
        </RangeSlider>
      </FilterParameters>
        
      <FilterParameters>
        <ParameterTitle>Team Size</ParameterTitle>
        <CheckboxGroup>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.unitSizes?.includes('2-5')}
              onChange={() => handleUnitSizeChange('2-5')}
            />
            2-5 Players
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.unitSizes?.includes('6-10')}
              onChange={() => handleUnitSizeChange('6-10')}
            />
            6-10 Players
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.unitSizes?.includes('11-20')}
              onChange={() => handleUnitSizeChange('11-20')}
            />
            11-20 Players
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox 
              type="checkbox"
              checked={filter.unitSizes?.includes('21+')}
              onChange={() => handleUnitSizeChange('21+')}
            />
            21+ Players
          </CheckboxLabel>
        </CheckboxGroup>
      </FilterParameters>
        
      <FilterButton onClick={applyFilters}>Apply Filters</FilterButton>
        
      <FilterStats>
        <div>Active Filters: {activeFiltersCount}</div>
        <div>Total Results: {totalCount}</div>
        <div>Match Rate: {activeFiltersCount > 0 ? '89%' : 'N/A'}</div>
      </FilterStats>
    </>
  );

  const sortedMissions = sortMissions(missions, sortBy);

  if (loading && missions.length === 0) {
    return (
      <PageBackground $shadow={isShadowGrid}>
        {isNightOpsMode && <DottedOverlay />}
        {isModernWarfare && <ModernBackground />}
        <Container>
          <Header>
            <PageTitle>Loading Missions...</PageTitle>
          </Header>
        </Container>
      </PageBackground>
    );
  }

  return (
    <PageBackground $shadow={isShadowGrid}>
      {isNightOpsMode && <DottedOverlay />}
      {isModernWarfare && <ModernBackground />}
      <Container>
        <Header>
          <PageTitle>Operation Database</PageTitle>
          <Subtitle>Discover and acquire tactical missions for your next gaming experience</Subtitle>
        </Header>

        <ControlsSection>
          <SearchAndSort>
            <SearchInput
              placeholder="Search missions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <SortSelect 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Sort by Name</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="difficulty">Difficulty Level</option>
            </SortSelect>
          </SearchAndSort>
        </ControlsSection>

        <MobileFilterHeader>
          <MobileFilterButton onClick={toggleMobileFilter}>
            Filters ({activeFiltersCount})
          </MobileFilterButton>
          <div style={{ fontSize: '14px', color: theme.colors.primary }}>
            {totalCount} missions found
          </div>
        </MobileFilterHeader>
        
        <MainContent>
          <FilterSection>
            <FilterContent />
          </FilterSection>
          
          <ContentArea>
            {loading ? (
              <LoadingSpinner>Loading missions...</LoadingSpinner>
            ) : error ? (
              <EmptyState>
                <h3>Error Loading Missions</h3>
                <p>{error}</p>
              </EmptyState>
            ) : sortedMissions.length === 0 ? (
              <EmptyState>
                <h3>No Missions Found</h3>
                <p>Try adjusting your search criteria or filters to find more missions.</p>
              </EmptyState>
            ) : (
              <MissionsGrid>
                {sortedMissions.map(mission => (
                  <MissionCard key={mission.id}>
                    <MissionLink to={`/operations/${mission.id}`}>
                      <MissionImage 
                        style={{ 
                          backgroundImage: mission.images?.length 
                            ? `url(${encodeURI(mission.images[0])})` 
                            : 'none'
                        }}
                      >
                        <MissionId>#{mission.id.substring(0, 8).toUpperCase()}</MissionId>
                        <MissionMatch>Featured</MissionMatch>
                        {!mission.images?.length && 'No Image Available'}
                      </MissionImage>
                      
                      <MissionInfo>
                        <MissionHeader>
                          <MissionCode>Mission-{mission.id.slice(-6).toUpperCase()}</MissionCode>
                          <MissionTitle>{mission.title || mission.name}</MissionTitle>
                        </MissionHeader>
                        <MissionDescription>
                          {mission.description || 'Tactical mission awaiting deployment'}
                        </MissionDescription>
                        {isFieldAuthenticated && (
                          <MissionFooter>
                            <MissionPrice>${mission.price.toLocaleString()}</MissionPrice>
                            <AddToCartButton
                              onClick={(e) => handleAddToCart(e, mission)}
                            >
                              Add to Cart
                            </AddToCartButton>
                          </MissionFooter>
                        )}
                      </MissionInfo>
                    </MissionLink>
                  </MissionCard>
                ))}
              </MissionsGrid>
            )}
          </ContentArea>
        </MainContent>
      </Container>

      {/* Mobile Filter Panel */}
      <FilterOverlay $isOpen={isMobileFilterOpen} onClick={closeMobileFilter} />
      <MobileFilterPanel $isOpen={isMobileFilterOpen}>
        <MobileFilterHeader2>
          <MobileFilterTitle>Mission Filters</MobileFilterTitle>
          <MobileFilterClose onClick={closeMobileFilter}>✕</MobileFilterClose>
        </MobileFilterHeader2>
        <FilterContent />
      </MobileFilterPanel>
    </PageBackground>
  );
};

export default MissionsPage;
